import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Bell, Menu, User, LogOut, Settings, FileText, Map, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Button from '@/components/ui/Button';
import StudyOfferNotification from '@/components/notifications/StudyOfferNotification';
import FieldCadastroModal from '@/components/field/FieldCadastroModal';
import LocationCacheButton from '@/components/location/LocationCacheButton';
import UnifiedMapCache from '@/components/maps/UnifiedMapCache';
import VisitedContactsMap from '@/components/maps/VisitedContactsMap';
import { NotificationListItem } from '@/services/notifications/NotificationsService';
import { dashboardService } from '@/services/dashboard/DashboardService';
import Modal from '@/components/ui/Modal';
import PulseService from '@/services/pulse/PulseService';
import Autocomplete from '@/components/ui/Autocomplete';
import { regionsService } from '@/services/regions/RegionsService';

interface HeaderProps {
  onMenuClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [showFieldCadastro, setShowFieldCadastro] = useState(false);
  const [showMapCache, setShowMapCache] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [isOnline, setIsOnline] = useState<boolean>(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [showVisitedMap, setShowVisitedMap] = useState(false);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [recentNotifications, setRecentNotifications] = useState<NotificationListItem[]>([]);
  const [onlineCount, setOnlineCount] = useState<number>(0);
  const [pulseStatus, setPulseStatus] = useState<{ connected: boolean; connecting: boolean }>({ connected: false, connecting: false });
  const [onlineModalOpen, setOnlineModalOpen] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState<Array<{ id: number; name?: string | null; team_name?: string | null; region_id?: number | null; region_name?: string | null; last_seen_at?: string | null }>>([]);
  const [onlineLoading, setOnlineLoading] = useState(false);
  const [onlineError, setOnlineError] = useState<string | null>(null);
  const [regionFilter, setRegionFilter] = useState<string | ''>('');
  const [regionOptions, setRegionOptions] = useState<{ value: string; label: string }[]>([]);
  const [regionOptionsLoading, setRegionOptionsLoading] = useState(false);
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [teamModalData, setTeamModalData] = useState<{ teamName: string; activatedBy: string } | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();

  // Espaço para funções do componente Header
  
  // Handle scroll to make header sticky
  const handleScroll = useCallback(() => {
    if (headerRef.current) {
      const scrollPosition = window.scrollY;
      const headerHeight = headerRef.current.offsetHeight;
      const headerSpacer = document.getElementById('header-spacer');
      
      // Tornar o header fixo após rolar além da altura do próprio header
      if (scrollPosition > headerHeight / 2) {
        setIsSticky(true);
        // Mostrar o espaço reservado quando o header está fixo
        if (headerSpacer) {
          headerSpacer.style.display = 'block';
        }
      } else {
        setIsSticky(false);
        // Esconder o espaço reservado quando o header não está fixo
        if (headerSpacer) {
          headerSpacer.style.display = 'none';
        }
      }
    }
  }, []);

  // Pulse subscriptions (replace SSE long-poll)
  useEffect(() => {
    // Example: team activation event
    const unsubTeam = PulseService.subscribe<any>('team_updates', (data) => {
      const teamName = (data?.team as any)?.name || 'Equipe';
      const activatedBy = (data?.activated_by as any)?.name || 'um membro';
      setTeamModalData({ teamName, activatedBy });
      setTeamModalOpen(true);
    });

    // Example: generic notifications changed
    const unsubNotif = PulseService.subscribe<any>('notifications:updated', () => {
      // trigger a refresh of counters/list
      refreshHeaderPulse();
    });

    // Connection status
    const unsubStatus = PulseService.subscribe<{ connected: boolean; connecting: boolean }>('pulse:status', (s) => {
      setPulseStatus(s);
    });

    return () => { unsubTeam(); unsubNotif(); unsubStatus(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load online count for user's region
  const refreshHeaderPulse = useCallback(async () => {
    // Avoid extra load on Teams page
    if (pathname === '/teams') return;
    try {
      const regionId = user?.region_id ?? null;
      const pulse = await dashboardService.pulse({
        regionId: regionId ?? undefined,
        include: 'notifications,unread',
        perPage: 10,
      });
      // Notifications
      const list = (pulse.notifications?.data || []) as unknown as NotificationListItem[];
      setRecentNotifications(list);
      setUnreadCount(pulse.unread?.count ?? 0);
      // Presence intentionally excluded here to avoid backend 500; we load full presence only when the modal opens
    } catch (_) {
      // silent fail
    }
  }, [pathname, user?.region_id]);

  // Load online users when modal opens or filter changes (kept separate due to different perPage needs)
  useEffect(() => {
    if (!onlineModalOpen) return;
    // Lazy load regions when modal opens (admin only needs it)
    if (user?.role === 'admin' && regionOptions.length === 0 && !regionOptionsLoading) {
      (async () => {
        setRegionOptionsLoading(true);
        try {
          const list = await regionsService.list();
          setRegionOptions((list || []).map((r: { id: number; name: string }) => ({ value: String(r.id), label: r.name })));
        } catch {
          // ignore
        } finally {
          setRegionOptionsLoading(false);
        }
      })();
    }
    const load = async () => {
      setOnlineLoading(true);
      setOnlineError(null);
      try {
        const rid = (regionFilter !== '' ? Number(regionFilter) : (user?.role === 'admin' ? null : (user?.region_id ?? null)));
        const pulse = await dashboardService.pulse({ regionId: rid ?? undefined, include: 'presence', perPage: 500 });
        const list = (((pulse as any).presence?.online_users) ?? []) as Array<any>;
        setOnlineUsers(list);
      } catch (err) {
        setOnlineError('Falha ao carregar usuários online');
      } finally {
        setOnlineLoading(false);
      }
    };
    void load();
  }, [onlineModalOpen, regionFilter, user?.role, user?.region_id, regionOptions.length, regionOptionsLoading]);

  // Add scroll event listener
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Online/Offline status listener
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setNotificationsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      // Ensure redirect to login even if route guard hasn't re-rendered yet
      navigate('/login');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  // Unified header pulse refresh

  useEffect(() => {
    // initial load
    refreshHeaderPulse();
    // periodic refresh
    const interval = window.setInterval(() => {
      refreshHeaderPulse();
    }, 30000); // 30s
    // react to global events triggered on actions
    const handler = () => refreshHeaderPulse();
    window.addEventListener('notifications:updated', handler as EventListener);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('notifications:updated', handler as EventListener);
    };
  }, [refreshHeaderPulse]);

  return (
    <>
    <header 
      ref={headerRef}
      className={`bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 ${isSticky ? 'fixed top-0 left-0 right-0 z-50 animate-slideDown shadow-md' : 'shadow-sm'}`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 justify-between items-center">
          {/* Left side - Mobile menu button */}
          <div className="flex items-center">
            <button
              type="button"
              className="lg:hidden -ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
              onClick={onMenuClick}
            >
              <span className="sr-only">Abrir menu</span>
              <Menu className="h-6 w-6" />
            </button>
          </div>

          {/* Right side - Field Cadastro, Notifications and user menu */}
          <div className="flex items-center space-x-2">
            {/* Field Cadastro Button */}
            <button
              type="button"
              className="relative p-1 rounded-full text-gray-400 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors group"
              onClick={() => {
                if (!user?.active_team_id) {
                  toast.warning('Você precisa ter uma equipe ativa para acessar o cadastro de campo');
                  navigate('/profile');
                } else {
                  setShowFieldCadastro(true);
                }
              }}
              title="Cadastro de Campo"
            >
              <span className="sr-only">Cadastro de Campo</span>
              <FileText className="h-6 w-6" />
              <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                Cadastro de Campo
              </span>
            </button>
            {/* Botão de Contatos Visitados no Mapa */}
            <button
              type="button"
              className="relative p-1 rounded-full text-gray-400 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors group"
              onClick={() => setShowVisitedMap(true)}
              title="Contatos Visitados no Mapa"
            >
              <span className="sr-only">Contatos Visitados no Mapa</span>
              <MapPin className="h-6 w-6" />
              <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                Visitados no Mapa
              </span>
            </button>
                        {/* Online users count (clickable) */}
            <div className="relative group">
              <button
                type="button"
                className="text-xs text-gray-600 min-w-[1.5rem] text-center underline decoration-dotted"
                onClick={() => setOnlineModalOpen(true)}
                title="Ver usuários online"
              >
                {onlineCount}
              </button>
              {/* Pulse status indicator */}
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${pulseStatus.connected ? 'bg-emerald-100 text-emerald-700' : (pulseStatus.connecting ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600')}`}
                    title={pulseStatus.connected ? 'Realtime conectado' : (pulseStatus.connecting ? 'Conectando ao Realtime...' : 'Realtime desconectado')}>
                {pulseStatus.connected ? 'RT OK' : (pulseStatus.connecting ? 'RT...' : 'RT OFF')}
              </span>
              <span className="absolute -bottom-7 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                {isOnline ? 'Online' : 'Offline'}
              </span>
            </div>

            {/* Study Offers Bell */}
            <StudyOfferNotification />

            {/* Notifications */}
            <div className="relative" ref={notificationsRef}>
              <button
                type="button"
                className="relative p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                onClick={() => setNotificationsOpen(!notificationsOpen)}
              >
                <span className="sr-only">Ver notificações</span>
                <Bell className="h-6 w-6" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </button>

              {/* Notifications dropdown */}
              {notificationsOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-white/10 z-50">
                  <div className="p-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">
                      Notificações
                    </h3>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {recentNotifications.map((notification) => (
                        <div
                          key={notification.id}
                          className={`p-3 rounded-md border ${
                            notification.read_at === null
                              ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700'
                              : 'bg-gray-50 border-gray-200 dark:bg-gray-700 dark:border-gray-600'
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {notification.title}
                              </p>
                              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                {notification.message}
                              </p>
                            </div>
                            {notification.read_at === null && (
                              <div className="w-2 h-2 bg-blue-500 rounded-full ml-2 mt-1" />
                            )}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            {new Date(notification.created_at).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-center"
                        onClick={() => { setNotificationsOpen(false); navigate('/notifications'); }}
                      >
                        Ver todas as notificações
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* User menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                type="button"
                className="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
              >
                <span className="sr-only">Abrir menu do usuário</span>
                <div className="h-8 w-8 rounded-full bg-primary-500 flex items-center justify-center">
                  <span className="text-sm font-medium text-white">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="ml-3 hidden md:block text-left">
                  <p className="text-sm font-medium text-gray-700">
                    {user?.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {user?.role === 'admin' && 'Administrador'}
                    {user?.role === 'leader' && 'Líder Regional'}
                    {user?.role === 'missionary' && 'Missionário'}
                  </p>
                </div>
              </button>

              {/* User dropdown */}
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-white/10 z-50">
                  <div className="py-1">
                    <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {user?.name}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {user?.email}
                      </p>
                    </div>
                    
                    <Link
                      to="/profile"
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <User className="h-4 w-4 mr-3" />
                      Perfil
                    </Link>
                    <button
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => { setShowMapCache(true); setUserMenuOpen(false); }}
                    >
                      <Map className="h-4 w-4 mr-3" />
                      Cache de Mapas
                    </button>
                    
                    <Link
                      to="/settings"
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Settings className="h-4 w-4 mr-3" />
                      Configurações
                    </Link>
                    
                    <div className="border-t border-gray-200 dark:border-gray-700">
                      <button
                        className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                        onClick={handleLogout}
                      >
                        <LogOut className="h-4 w-4 mr-3" />
                        Sair
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Field Cadastro Modal */}
      <FieldCadastroModal 
        isOpen={showFieldCadastro} 
        onClose={() => setShowFieldCadastro(false)} 
      />
      
      {/* Map Cache Modal */}
      <UnifiedMapCache
        isOpen={showMapCache}
        onClose={() => setShowMapCache(false)}
      />

      {/* Visited Contacts Map Modal */}
      <VisitedContactsMap
        isOpen={showVisitedMap}
        onClose={() => setShowVisitedMap(false)}
      />

      {/* Team Activation Modal (SSE) */}
      {teamModalOpen && teamModalData && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Equipe ativada</h3>
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
              A equipe "{teamModalData.teamName}" foi ativada por {teamModalData.activatedBy}.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setTeamModalOpen(false)}>Fechar</Button>
              <Link to="/teams">
                <Button>Ir para Equipes</Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>

    {/* Online users modal */}
    <Modal isOpen={onlineModalOpen} onClose={() => setOnlineModalOpen(false)} title="Usuários online" size="md">
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs text-gray-600 dark:text-gray-300">
            <div>Sua região: <span className="font-medium">{user?.region_id ?? '-'}</span></div>
            {user?.address && (
              <div>Última localidade: <span className="font-medium">{String((user as any).address)}</span></div>
            )}
          </div>
          {user?.role === 'admin' && (
            <div className="min-w-[16rem]">
              <Autocomplete
                label="Filtrar por região"
                value={regionFilter || ''}
                onChange={(val) => {
                  if (!val) { setRegionFilter(''); return; }
                  setRegionFilter(String(val));
                }}
                options={regionOptions}
                placeholder="Selecione uma região"
                allowCustom={false}
              />
            </div>
          )}
        </div>
        {onlineLoading && <div className="text-sm text-gray-500 dark:text-gray-400">Carregando...</div>}
        {onlineError && <div className="text-sm text-red-600 dark:text-red-400">{onlineError}</div>}
        {!onlineLoading && !onlineError && (
          <div className="max-h-80 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-200">
                <tr>
                  <th className="px-3 py-2 text-left">Nome</th>
                  <th className="px-3 py-2 text-left">Equipe</th>
                  <th className="px-3 py-2 text-left">Regional</th>
                  <th className="px-3 py-2 text-left">Visto</th>
                </tr>
              </thead>
              <tbody>
                {onlineUsers.length === 0 ? (
                  <tr><td className="px-3 py-3 text-gray-500 dark:text-gray-400" colSpan={4}>Nenhum usuário online.</td></tr>
                ) : (
                  onlineUsers.map(u => (
                    <tr key={u.id} className="odd:bg-white even:bg-gray-50 dark:odd:bg-gray-800 dark:even:bg-gray-750/30">
                      <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{u.name ?? `#${u.id}`}</td>
                      <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{u.team_name ?? '-'}</td>
                      <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{u.region_name ?? (u.region_id ?? '-')}</td>
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{u.last_seen_at ? new Date(u.last_seen_at).toLocaleString() : '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => setOnlineModalOpen(false)}>Fechar</Button>
        </div>
      </div>
    </Modal>
  </>
  );
};

export default Header;
